import { Modal, useModals } from 'almanac-ui/Modal'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { Button } from 'components/Button'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { useHistory } from 'react-router-dom'
import Styles from './ManageIntegrationAccounts.module.scss'
import { ConnectedAccountCard } from './ConnectedAccountCard'
import { integrationToName } from 'components/IntegrationRequiredModal/IntegrationRequiredModal'
import { Add } from 'almanac-ui/icons'
import { PlatformIcon } from 'screens/ScheduledPosts/PlatformIcon'
import { AddNewAccountCard } from './AddNewAccountCard'
import XInitialization from 'components/IntegrationInitialization/XInitialization/XInitialization'
import {
  FacebookInitialization,
  InstagramInitialization,
  LinkedinInitialization,
} from 'components/IntegrationInitialization'
import WordPressInitialization from 'components/IntegrationInitialization/WordPressInitialization/WordPressInitialization'
import { useInitiateOauth } from 'hooks/useInitiateOauth'
import YouTubeInitialization from '../../IntegrationInitialization/YouTubeInitialization/YouTubeInitialization'
import MailchimpInitialization from 'components/IntegrationInitialization/MailchimpInitialization/MailchimpInitialization'
import { useTierUsageLimits } from 'hooks/useTierUsageLimits'
import { TierUpgradeModal } from 'components/TierUpgradeModal/TierUpgradeModal'
import { capitalize } from 'lodash'

export const ManageIntegrationAccounts = ({ provider }: { provider: ExternalIntegrationEnum }) => {
  const { data, isLoading } = useExternalIntegrations()
  const history = useHistory()
  const name = integrationToName[provider]
  const integrations = data?.external_integrations?.filter((integration) => integration.provider === provider) || []
  const { openModal, closeModal } = useModals()
  const { initiateOauth } = useInitiateOauth()
  const { atLimit, nextTierPlan, currentTierPlan } = useTierUsageLimits({
    limitKey: 'integrations',
    integrationType: provider,
  })

  const handlePressOutside = () => {
    history.push({ search: `` })
    closeModal()
  }

  const shouldEnforceLimit = atLimit && nextTierPlan && currentTierPlan

  const handleLimitWrappedPress = () => {
    if (shouldEnforceLimit) {
      openModal(TierUpgradeModal, {
        reason: 'limit_reached',
        feature: `${capitalize(provider)} Integrations`,
        upgradeTier: nextTierPlan,
        currentTier: currentTierPlan,
        onUpgrade: handleConnectAccount,
      })
    } else {
      handleConnectAccount()
    }
  }

  const handleConnectAccount = () => {
    switch (provider) {
      case ExternalIntegrationEnum.LinkedIn:
        openModal(LinkedinInitialization)
        break
      case ExternalIntegrationEnum.X:
        openModal(XInitialization)
        break
      case ExternalIntegrationEnum.Facebook:
        openModal(FacebookInitialization)
        break
      case ExternalIntegrationEnum.Instagram:
        openModal(InstagramInitialization)
        break
      case ExternalIntegrationEnum.WordPress:
        openModal(WordPressInitialization)
        break
      case ExternalIntegrationEnum.TikTok:
        initiateOauth(ExternalIntegrationEnum.TikTok)
        break
      case ExternalIntegrationEnum.YouTube:
        openModal(YouTubeInitialization)
        break
      case ExternalIntegrationEnum.Mailchimp:
        openModal(MailchimpInitialization)
        break
      case ExternalIntegrationEnum.GoHighLevel:
        initiateOauth(ExternalIntegrationEnum.GoHighLevel)
        break
      default:
        break
    }
  }

  return (
    <Modal.Root size="md" onPressOutside={handlePressOutside}>
      <Modal.Header
        title={
          <div className={Styles.title}>
            <Modal.BackButton onPress={closeModal} />
            <PlatformIcon platform={provider} size={24} />
            {name} Accounts
          </div>
        }
        actions={
          <Button
            size="lg"
            onPress={handleLimitWrappedPress}
            variant="primary"
            frontIcon={Add}
            data-testid="connect-account-button"
          >
            Connect Another {name} Account
          </Button>
        }
      />
      <Modal.Content>
        {isLoading ? (
          'Loading...'
        ) : (
          <div className={Styles.container}>
            {integrations.map((integration) => (
              <ConnectedAccountCard key={integration.id} integration={integration} />
            ))}
            <AddNewAccountCard provider={provider} onPress={handleConnectAccount} />
          </div>
        )}
      </Modal.Content>
    </Modal.Root>
  )
}
